import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
// Pré-carregadas em paralelo com o CSS (ver src/routes/__root.tsx head.links)
// porque são a fonte do <h1> da home (elemento de LCP) e do parágrafo logo
// abaixo — sem preload, o navegador só as descobre depois de baixar/interpretar
// o CSS inteiro, serializando a cadeia crítica.
import montserrat800 from "@fontsource/montserrat/files/montserrat-latin-800-normal.woff2?url";
import roboto400 from "@fontsource/roboto/files/roboto-latin-400-normal.woff2?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { initSentry, captureClientError } from "../lib/sentry-browser";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { WhatsappFloat } from "@/components/whatsapp-float";
import { CartDrawer } from "@/components/cart-drawer";
import { CookieConsentBanner } from "@/components/cookie-consent-banner";
import { AnalyticsPageViewTracker } from "@/components/analytics-page-view-tracker";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { getConsent, loadGA4 } from "@/lib/analytics";
import { storeSettingsQuery } from "@/lib/settings.functions";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
    captureClientError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  loader: async ({ context }) => context.queryClient.ensureQueryData(storeSettingsQuery()),
  head: ({ loaderData }) => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Loja EG Elétrica — Painéis Elétricos" },
      {
        name: "description",
        content:
          "Catálogo de painéis elétricos residenciais, comerciais, industriais e sob medida. Compra online com segurança.",
      },
      { name: "author", content: "EG Elétrica" },
      { property: "og:title", content: "Loja EG Elétrica — Painéis Elétricos" },
      {
        property: "og:description",
        content:
          "Catálogo de painéis elétricos residenciais, comerciais, industriais e sob medida. Compra online com segurança.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Loja EG Elétrica — Painéis Elétricos" },
      {
        name: "twitter:description",
        content:
          "Catálogo de painéis elétricos residenciais, comerciais, industriais e sob medida. Compra online com segurança.",
      },
      {
        property: "og:image",
        content:
          "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/aca544da-e53e-433b-a383-884085422f21/id-preview-8f2c2aee--af7dba5a-62bd-448b-bb4e-5e6c50a2c66d.lovable.app-1784993560036.png",
      },
      {
        name: "twitter:image",
        content:
          "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/aca544da-e53e-433b-a383-884085422f21/id-preview-8f2c2aee--af7dba5a-62bd-448b-bb4e-5e6c50a2c66d.lovable.app-1784993560036.png",
      },
    ],
    links: [
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: montserrat800,
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: roboto400,
        crossOrigin: "anonymous",
      },
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", href: loaderData?.favicon_url ?? "/favicon.ico" },
    ],
    scripts: loaderData
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Store",
              name: loaderData.nome_fantasia,
              url: loaderData.institutional_url,
              logo: loaderData.logo_url ?? undefined,
              telephone: loaderData.whatsapp_number,
              address: loaderData.endereco
                ? {
                    "@type": "PostalAddress",
                    streetAddress: loaderData.endereco,
                    addressLocality: loaderData.cidade,
                    addressRegion: loaderData.uf,
                    postalCode: loaderData.cep ?? undefined,
                    addressCountry: "BR",
                  }
                : undefined,
            }),
          },
        ]
      : [],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="pt-BR">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  useEffect(() => {
    void useCart.persist.rehydrate();
    useAuth.getState().init();
    if (getConsent() === "granted") loadGA4();
    initSentry();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex min-h-screen flex-col bg-eg-gray">
        <SiteHeader />
        <main className="flex-1">
          <Outlet />
        </main>
        <SiteFooter />
        <CartDrawer />
        <WhatsappFloat />
        <CookieConsentBanner />
        <AnalyticsPageViewTracker />
      </div>
    </QueryClientProvider>
  );
}
